// src/components/common/Layout/WebsiteLayout.js
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Navbar from "../../../components/common/Layout/Navbar";
import "./WebsiteAmbient.css";
import "./Websitelayout.css";

const WebsiteLayout = ({
  children,
  pageMeta = {},
  hideCookieConsent = false,
}) => {
  // Default metadata
  const defaultMeta = {
    title: "Toora Flex | Smart Digital Solutions for South African Businesses",
    description:
      "Comprehensive digital solutions for modern businesses in South Africa. We design, develop, and deploy solutions that help your business grow.",
    keywords:
      "digital solutions, web design, web development, hosting, e-commerce, South Africa, Pretoria, Toora Flex, business solutions, IT services",
    image: "https://tooraflex.co.za/images/og-image.jpg",
    url: "https://tooraflex.co.za",
    type: "website",
    author: "Toora Flex",
    siteName: "Toora Flex",
    twitterHandle: "@tooraflex",
    locale: "en_ZA",
    publishedTime: null,
    modifiedTime: null,
    tags: [],
  };

  // Merge default with page-specific meta
  const meta = { ...defaultMeta, ...pageMeta };
  const fullTitle =
    meta.title === defaultMeta.title
      ? meta.title
      : `${meta.title} | Toora Flex`;

  // Update document title
  useEffect(() => {
    document.title = fullTitle;

    // Update meta description dynamically
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", meta.description);
    }
  }, [fullTitle, meta.description]);

  const currentYear = new Date().getFullYear();

  // Structured Data - Organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Toora Flex",
    url: "https://tooraflex.co.za",
    logo: "https://tooraflex.co.za/images/logo/logo.ico",
    description:
      "Comprehensive digital solutions for modern businesses in South Africa",
    address: {
      "@type": "PostalAddress",
      streetAddress: "012 Central, 367 Helen Joseph Street",
      addressLocality: "Pretoria CBD",
      addressRegion: "Gauteng",
      postalCode: "0001",
      addressCountry: "ZA",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+27-66-457-5904",
      contactType: "sales",
      availableLanguage: ["English"],
      areaServed: "ZA",
    },
    sameAs: [
      "https://www.facebook.com/tooraflex",
      "https://twitter.com/tooraflex",
      "https://www.linkedin.com/company/tooraflex",
      "https://www.instagram.com/tooraflex",
    ],
  };

  // Structured Data - Website
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Toora Flex",
    url: "https://tooraflex.co.za",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://tooraflex.co.za/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  // Structured Data - Breadcrumb (if path is provided)
  const getBreadcrumbSchema = (path) => {
    if (!path || path === "/") return null;

    const pathSegments = path.split("/").filter((segment) => segment);
    const items = [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: "https://tooraflex.co.za/",
      },
    ];

    let currentPath = "";
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      items.push({
        "@type": "ListItem",
        position: index + 2,
        name: segment.charAt(0).toUpperCase() + segment.slice(1),
        item: `https://tooraflex.co.za${currentPath}`,
      });
    });

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items,
    };
  };

  const breadcrumbSchema = getBreadcrumbSchema(meta.path);

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{fullTitle}</title>
        <meta name="description" content={meta.description} />
        <meta name="keywords" content={meta.keywords} />
        <meta name="author" content={meta.author} />
        <meta name="robots" content="index, follow" />
        <meta name="googlebot" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Canonical URL */}
        <link rel="canonical" href={meta.url} />

        {/* Alternate language versions */}
        <link rel="alternate" href={meta.url} hrefLang="en-za" />
        <link rel="alternate" href={meta.url} hrefLang="x-default" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content={meta.type} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={meta.image} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={meta.url} />
        <meta property="og:site_name" content={meta.siteName} />
        <meta property="og:locale" content={meta.locale} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />
        <meta name="twitter:site" content={meta.twitterHandle} />
        <meta name="twitter:creator" content={meta.twitterHandle} />

        {/* Article specific meta tags */}
        {meta.type === "article" && (
          <>
            {meta.publishedTime && (
              <meta
                property="article:published_time"
                content={meta.publishedTime}
              />
            )}
            {meta.modifiedTime && (
              <meta
                property="article:modified_time"
                content={meta.modifiedTime}
              />
            )}
            {meta.tags &&
              meta.tags.map((tag, index) => (
                <meta key={index} property="article:tag" content={tag} />
              ))}
          </>
        )}

        {/* Business / Organization specific */}
        <meta name="geo.region" content="ZA-GT" />
        <meta name="geo.placename" content="Pretoria" />
        <meta name="geo.position" content="-25.7479;28.2293" />
        <meta name="ICBM" content="-25.7479, 28.2293" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        {breadcrumbSchema && (
          <script type="application/ld+json">
            {JSON.stringify(breadcrumbSchema)}
          </script>
        )}
      </Helmet>

      <div className="website-layout">
        <Navbar variant="public" />

        <main>
          {/* Skip to content link for accessibility */}
          <a
            href="#main-content"
            className="skip-to-content"
            onClick={(e) => {
              e.preventDefault();
              const mainContent = document.getElementById("main-content");
              if (mainContent) {
                mainContent.setAttribute("tabindex", "-1");
                mainContent.focus();
                // Remove tabindex after focus to maintain clean HTML
                setTimeout(() => {
                  mainContent.removeAttribute("tabindex");
                }, 1000);
              }
            }}
          >
            Skip to main content
          </a>

          <div id="main-content" role="main" aria-label="Main content">
            {children}
          </div>
        </main>

        {/* Footer */}

        <footer id="main-footer" role="contentinfo" aria-label="Site footer">
          <div className="container">
            <div className="footer-content">
              {/* Column 1 - Brand */}
              <div className="footer-col footer-brand-col">
                <div className="footer-brand">
                  <img
                    src="/images/logo/logo.ico"
                    alt="Toora Flex - Smart Digital Solutions"
                    className="footer-logo"
                    width="40"
                    height="40"
                    loading="lazy"
                  />
                  <h3>
                    <span className="toora-color">Toora</span>
                    <span className="flex-color">Flex</span>
                  </h3>
                </div>
                <p>
                  Comprehensive digital solutions for modern businesses. We
                  design, develop, and deploy solutions that help your business
                  grow.
                </p>
                <div className="social-links" aria-label="Social media links">
                  <a
                    href="https://www.facebook.com/tooraflex"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Facebook"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                    </svg>
                  </a>
                  <a
                    href="https://twitter.com/tooraflex"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Twitter"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path>
                    </svg>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/tooraflex"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on LinkedIn"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                      <rect x="2" y="9" width="4" height="12"></rect>
                      <circle cx="4" cy="4" r="2"></circle>
                    </svg>
                  </a>
                  <a
                    href="https://www.instagram.com/tooraflex"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Follow us on Instagram"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect
                        x="2"
                        y="2"
                        width="20"
                        height="20"
                        rx="5"
                        ry="5"
                      ></rect>
                      <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                    </svg>
                  </a>
                </div>
              </div>

              {/* Column 2 - Quick Links */}
              <div className="footer-col">
                <h3>Quick Links</h3>
                <ul
                  className="footer-links"
                  role="navigation"
                  aria-label="Quick links"
                >
                  <li>
                    <Link to="/">Home</Link>
                  </li>
                  <li>
                    <Link to="/services">Services & Products</Link>
                  </li>
                  <li>
                    <Link to="/about">About Us</Link>
                  </li>
                  <li>
                    <Link to="/contact">Contact</Link>
                  </li>
                  <li>
                    <Link to="/blog">Blog</Link>
                  </li>
                  <li>
                    <Link to="/portfolio">Portfolio</Link>
                  </li>
                  <li>
                    <Link to="/faq">FAQ</Link>
                  </li>
                </ul>
              </div>

              {/* Column 3 - Our Services */}
              <div className="footer-col">
                <h3>Our Services</h3>
                <ul
                  className="footer-links"
                  role="navigation"
                  aria-label="Our services"
                >
                  <li>
                    <Link to="/services#web-design">Website Design</Link>
                  </li>
                  <li>
                    <Link to="/services#web-applications">
                      Web Applications
                    </Link>
                  </li>
                  <li>
                    <Link to="/services#hosting">Hosting Solutions</Link>
                  </li>
                  <li>
                    <Link to="/services#graphic-design">Graphic Design</Link>
                  </li>
                  <li>
                    <Link to="/ecommerce">E-Commerce</Link>
                  </li>
                </ul>
              </div>

              {/* Column 4 - Contact Info */}
              <div className="footer-col footer-contact-col">
                <h3>Contact Info</h3>
                <ul className="footer-links contact-info">
                  <li>
                    <svg
                      className="footer-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                    <span>
                      012 Central, 367 Helen Joseph Street, Pretoria CBD
                    </span>
                  </li>
                  <li>
                    <svg
                      className="footer-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                    </svg>
                    <a href="tel:+27664575904">+27 66 457 5904</a>
                  </li>
                  <li>
                    <svg
                      className="footer-icon"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                      <polyline points="22,6 12,13 2,6"></polyline>
                    </svg>
                    <a href="mailto:info@tooraflex.co.za">
                      info@tooraflex.co.za
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Legal & Policy Links */}
            <div className="footer-legal">
              <ul className="footer-legal-links">
                <li>
                  <Link to="/privacy">Privacy Policy</Link>
                </li>
                <li>
                  <a
                    href="/sitemap.xml"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Sitemap
                  </a>
                </li>
              </ul>
            </div>

            {/* Copyright */}
            <div className="copyright">
              <p>
                <svg
                  className="copyright-icon"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M15 9.354a4 4 0 1 0 0 5.292"></path>
                </svg>
                {currentYear} <span className="toora-color">Toora</span>
                <span className="flex-color">Flex</span>. All rights reserved.
              </p>
            </div>
          </div>
        </footer>

        {/* Cookie Consent */}
        {!hideCookieConsent && (
          <div
            id="cookie-consent"
            className="cookie-consent"
            role="dialog"
            aria-label="Cookie consent"
          >
            <div className="cookie-content">
              <div className="cookie-text">
                <h3>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5 10 10 0 0 0 0 0z"></path>
                    <path d="M8.5 8.5v.01"></path>
                    <path d="M16 15.5v.01"></path>
                    <path d="M12 12v.01"></path>
                    <path d="M11 17v.01"></path>
                    <path d="M7 14v.01"></path>
                  </svg>
                  Cookie Consent
                </h3>
                <p>
                  We use cookies to improve your experience on our site. By
                  clicking "Accept All", you agree to the use of all cookies as
                  per POPIA.
                </p>
              </div>
              <div className="cookie-buttons">
                <button
                  id="cookie-accept-all"
                  className="btn cookie-btn accept-btn"
                  aria-label="Accept all cookies"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                  Accept All
                </button>
                <button
                  id="cookie-necessary"
                  className="btn cookie-btn necessary-btn"
                  aria-label="Accept necessary cookies only"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="3"></circle>
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                  </svg>
                  Necessary Only
                </button>
                <button
                  id="cookie-decline"
                  className="btn cookie-btn decline-btn"
                  aria-label="Decline all cookies"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Decline
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default WebsiteLayout;
